import { useTranslation } from 'react-i18next';
import type { ChargePointData } from '../../types/charge-point.types';
import {
  getPriceTypeKey,
  generateSpotPriceHourSpan,
  formatPrice,
  getCurrencyDisplay,
} from '../../utils/price-utils';
import { DynamicPriceForecast } from './DynamicPriceForecast';

interface PricingSectionProps {
  chargePoint: ChargePointData;
  isLoading?: boolean;
}

/**
 * Display pricing information including dynamic price forecasts
 */
export function PricingSection({
  chargePoint,
  isLoading = false,
}: PricingSectionProps) {
  const { t, i18n } = useTranslation();

  const pricingCollection = chargePoint.pricingCollection;

  // Prefer user pricing over public pricing
  const pricing =
    pricingCollection?.pricingsUser ?? pricingCollection?.pricingsPublic;

  // Don't show section if no pricing info
  if (!pricing) {
    return null;
  }

  const priceType = pricing.masterPricing?.type;
  const isSpotPrice = priceType === 'spot';
  const dynamicEntries = pricing.dynamicPriceEntries;
  const currency = pricing.currency;

  // Show current time slot for spot pricing
  const currentTimeSlot =
    isSpotPrice &&
    dynamicEntries &&
    dynamicEntries.length > 0 &&
    dynamicEntries[0]
      ? generateSpotPriceHourSpan(dynamicEntries[0].time, i18n.language)
      : null;

  return (
    <div className="space-y-3">
      <div className="p-6 bg-ink/[0.03] rounded-2xl space-y-4">
        {/* Price type explanation */}
        <p className="text-sm text-ink/80">
          {t('chargePoint.priceTypeExplanation', {
            priceType: t(getPriceTypeKey(priceType)),
            operator:
              chargePoint.operatorName || t('chargePoint.unknownOperator'),
          })}
        </p>

        {/* Spot price explanation */}
        {isSpotPrice && (
          <p className="text-sm text-ink/80">
            {t('chargePoint.spotPriceExplanation')}
          </p>
        )}

        {/* Current price display for spot pricing */}
        {isSpotPrice && pricingCollection?.publicLabel && (
          <div className="text-center space-y-1">
            <p className="text-sm text-muted">
              {t('chargePoint.currentPrice')}
              {currentTimeSlot && <span> ({currentTimeSlot})</span>}
            </p>
            <div className="text-3xl font-bold text-ink">
              {pricingCollection.publicLabel}
            </div>
          </div>
        )}

        {/* Dynamic forecast or skeleton */}
        {isSpotPrice && (
          <>
            {isLoading || !dynamicEntries || dynamicEntries.length === 0 ? (
              // Skeleton loader for chart
              <div className="border-l-2 border-orange-500 pl-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="h-4 w-40 bg-ink/10 rounded animate-pulse"></div>
                  <div className="h-8 w-20 bg-ink/10 rounded-lg animate-pulse"></div>
                </div>
                <div className="w-full h-48 bg-ink/10 rounded animate-pulse"></div>
              </div>
            ) : (
              <DynamicPriceForecast
                entries={dynamicEntries}
                currency={currency}
              />
            )}
          </>
        )}

        {/* Fixed price display */}
        {!isSpotPrice && pricing.masterPricing && (
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-ink">
              {formatPrice(
                pricing.masterPricing.amount,
                currency,
                i18n.language
              )}
            </span>
            <span className="text-sm text-ink/80">
              {getCurrencyDisplay(currency)}/kWh
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
